/**
 * Quick Expense Entry Panel for Future Closure Calculator
 * 
 * Allows users to enter/update monthly expense values directly
 * to maximize claim recovery. Shows source badges and impact preview.
 * 
 * Phase 1 Implementation:
 * - Collapsible section in Future Closure form
 * - Fields: Rent, Utilities, Officer Salary, Insurance, Other Fixed
 * - Live impact preview
 * - Save to report
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FinancialValue } from './shared/FinancialValue';
import { DollarSign, ChevronDown, ChevronUp, Save, AlertCircle, TrendingUp, Info } from 'lucide-react';
import api from '../utils/api';

interface ExpenseData {
  officer_salary: number;
  rent: number;
  utilities: number;
  insurance: number;
  other_fixed: number;
}

interface ExpenseSource {
  officer_salary: 'verified' | 'estimate' | 'template';
  rent: 'verified' | 'estimate' | 'template';
  utilities: 'verified' | 'estimate' | 'template';
  insurance: 'verified' | 'estimate' | 'template';
  other_fixed: 'verified' | 'estimate' | 'template';
}

interface QuickExpenseEntryPanelProps {
  reportId: string;
  reportData?: any;
  closureDurationMonths?: number;
  onExpensesChange?: (expenses: ExpenseData, sources: ExpenseSource) => void;
  onSave?: (expenses: ExpenseData, sources: ExpenseSource) => Promise<void>;
}

// Industry template defaults (BLS median values as % of revenue)
const INDUSTRY_DEFAULTS: Record<string, { 
  officer_salary: number; 
  rent: number; 
  utilities: number; 
  insurance: number; 
  other_fixed: number 
}> = {
  restaurant: { officer_salary: 0.08, rent: 0.08, utilities: 0.025, insurance: 0.015, other_fixed: 0.05 },
  retail: { officer_salary: 0.06, rent: 0.10, utilities: 0.02, insurance: 0.012, other_fixed: 0.04 },
  manufacturing: { officer_salary: 0.05, rent: 0.06, utilities: 0.04, insurance: 0.02, other_fixed: 0.06 },
  service: { officer_salary: 0.10, rent: 0.07, utilities: 0.02, insurance: 0.015, other_fixed: 0.04 },
  healthcare: { officer_salary: 0.12, rent: 0.08, utilities: 0.025, insurance: 0.03, other_fixed: 0.05 },
  default: { officer_salary: 0.07, rent: 0.08, utilities: 0.025, insurance: 0.015, other_fixed: 0.05 }
};

const SOURCE_BADGES: Record<string, { label: string; color: React.CSSProperties; dotColor: string }> = {
  verified: { label: 'Verified', color: { backgroundColor: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' }, dotColor: 'var(--color-verified)' },
  estimate: { label: 'Client Estimate', color: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' }, dotColor: 'var(--color-warning)' },
  template: { label: 'Industry Default', color: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }, dotColor: 'var(--color-text-dim)' }
};

export default function QuickExpenseEntryPanel({
  reportId,
  reportData,
  closureDurationMonths = 36,
  onExpensesChange,
  onSave
}: QuickExpenseEntryPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expenses, setExpenses] = useState<ExpenseData>({
    officer_salary: 0,
    rent: 0,
    utilities: 0,
    insurance: 0,
    other_fixed: 0
  });
  const [sources, setSources] = useState<ExpenseSource>({
    officer_salary: 'template',
    rent: 'template',
    utilities: 'template',
    insurance: 'template',
    other_fixed: 'template'
  });
  const [originalExpenses, setOriginalExpenses] = useState<ExpenseData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Calculate average monthly revenue from report data
  const getAverageMonthlyRevenue = useCallback(() => {
    if (!reportData?.financial_data) return 50000; // Default fallback
    
    let totalRevenue = 0;
    let monthCount = 0;
    
    const financialData = reportData.financial_data;
    for (const key in financialData) {
      if (key.startsWith('data_') && Array.isArray(financialData[key])) {
        for (const month of financialData[key]) {
          totalRevenue += month.revenue || 0;
          monthCount++;
        }
      }
    }
    
    return monthCount > 0 ? totalRevenue / monthCount : 50000;
  }, [reportData]);

  // Extract current expenses from report data
  const extractCurrentExpenses = useCallback(() => {
    if (!reportData?.financial_data) return null;
    
    let totalOfficer = 0;
    let totalRent = 0;
    let totalUtilities = 0;
    let totalInsurance = 0;
    let totalOther = 0;
    let monthCount = 0;
    
    const financialData = reportData.financial_data;
    for (const key in financialData) {
      if (key.startsWith('data_') && Array.isArray(financialData[key])) {
        for (const month of financialData[key]) {
          // Extract payroll
          const payroll = month.payroll || {};
          totalOfficer += payroll.officer_comp || payroll.officer_salary || 0;
          
          // Extract expenses
          const exp = month.expenses || {};
          totalRent += exp.Rent || exp.rent || 0;
          totalUtilities += exp.Utilities || exp.utilities || 0;
          totalInsurance += exp.Insurance || exp.insurance || 0;
          totalOther += exp['Other Expenses'] || exp.other_expenses || exp.Other || 0;
          
          monthCount++;
        }
      }
    }
    
    if (monthCount === 0) return null;
    
    return {
      officer_salary: Math.round(totalOfficer / monthCount),
      rent: Math.round(totalRent / monthCount),
      utilities: Math.round(totalUtilities / monthCount),
      insurance: Math.round(totalInsurance / monthCount),
      other_fixed: Math.round(totalOther / monthCount)
    };
  }, [reportData]);

  // Initialize expenses from report data or templates
  useEffect(() => {
    const currentExpenses = extractCurrentExpenses();
    const avgRevenue = getAverageMonthlyRevenue();
    const businessType = (reportData?.business_type || 'default').toLowerCase();
    const defaults = INDUSTRY_DEFAULTS[businessType] || INDUSTRY_DEFAULTS.default;
    
    // Calculate template-based defaults
    const templateExpenses: ExpenseData = {
      officer_salary: Math.round(avgRevenue * defaults.officer_salary),
      rent: Math.round(avgRevenue * defaults.rent),
      utilities: Math.round(avgRevenue * defaults.utilities),
      insurance: Math.round(avgRevenue * defaults.insurance),
      other_fixed: Math.round(avgRevenue * defaults.other_fixed)
    };
    
    // Use current data if available, otherwise use templates
    const initialExpenses: ExpenseData = {
      officer_salary: currentExpenses?.officer_salary || templateExpenses.officer_salary,
      rent: currentExpenses?.rent || templateExpenses.rent,
      utilities: currentExpenses?.utilities || templateExpenses.utilities,
      insurance: currentExpenses?.insurance || templateExpenses.insurance,
      other_fixed: currentExpenses?.other_fixed || templateExpenses.other_fixed
    };
    
    // Set sources based on whether data exists
    const initialSources: ExpenseSource = {
      officer_salary: currentExpenses?.officer_salary ? 'estimate' : 'template',
      rent: currentExpenses?.rent ? 'estimate' : 'template',
      utilities: currentExpenses?.utilities ? 'estimate' : 'template',
      insurance: currentExpenses?.insurance ? 'estimate' : 'template',
      other_fixed: currentExpenses?.other_fixed ? 'estimate' : 'template'
    };
    
    setExpenses(initialExpenses);
    setSources(initialSources);
    setOriginalExpenses(initialExpenses);
  }, [reportData, extractCurrentExpenses, getAverageMonthlyRevenue]);

  // Calculate impact when expenses change
  const calculateTotalMonthlyExpenses = () => {
    return expenses.officer_salary + expenses.rent + expenses.utilities + 
           expenses.insurance + expenses.other_fixed;
  };

  const calculateTotalClaimImpact = () => {
    return calculateTotalMonthlyExpenses() * closureDurationMonths;
  };

  const calculateChangeFromOriginal = () => {
    if (!originalExpenses) return 0;
    const originalTotal = originalExpenses.officer_salary + originalExpenses.rent + 
                          originalExpenses.utilities + originalExpenses.insurance + 
                          originalExpenses.other_fixed;
    const newTotal = calculateTotalMonthlyExpenses();
    return (newTotal - originalTotal) * closureDurationMonths;
  };

  // Handle expense change
  const handleExpenseChange = (field: keyof ExpenseData, value: string) => {
    const numValue = parseFloat(value) || 0;
    const newExpenses = { ...expenses, [field]: numValue };
    const newSources = { ...sources, [field]: 'estimate' as const };
    
    setExpenses(newExpenses);
    setSources(newSources);
    setHasUnsavedChanges(true);
    
    if (onExpensesChange) {
      onExpensesChange(newExpenses, newSources);
    }
  };

  // Upgrade source to verified
  const handleUpgradeToVerified = (field: keyof ExpenseSource) => {
    const newSources = { ...sources, [field]: 'verified' as const };
    setSources(newSources);
    setHasUnsavedChanges(true);
  };

  // Apply industry template (fetch from backend)
  const handleApplyTemplate = async () => {
    const avgRevenue = getAverageMonthlyRevenue();
    const businessType = (reportData?.business_type || 'default').toLowerCase().replace(/\s+/g, '_');
    
    try {
      // Try to fetch from backend first
      const data = await api.get(
        `/api/v1/templates/${businessType}/future-closure-defaults?monthly_revenue=${avgRevenue}`,
        { showErrorToast: false }
      );
      
      const templateExpenses: ExpenseData = {
        officer_salary: data.monthly_amounts.officer_salary,
        rent: data.monthly_amounts.rent,
        utilities: data.monthly_amounts.utilities,
        insurance: data.monthly_amounts.insurance,
        other_fixed: data.monthly_amounts.other_fixed
      };
      
      setExpenses(templateExpenses);
      setSources({
        officer_salary: 'template',
        rent: 'template',
        utilities: 'template',
        insurance: 'template',
        other_fixed: 'template'
      });
      setHasUnsavedChanges(true);
      
      console.log(`✅ Applied ${data.template_name} defaults from ${data.source}`);
      
      if (onExpensesChange) {
        onExpensesChange(templateExpenses, {
          officer_salary: 'template',
          rent: 'template',
          utilities: 'template',
          insurance: 'template',
          other_fixed: 'template'
        });
      }
      return;
    } catch (err) {
      console.warn('Failed to fetch template defaults from backend, using local fallback');
    }
    
    // Fallback to local defaults
    const defaults = INDUSTRY_DEFAULTS[businessType] || INDUSTRY_DEFAULTS.default;
    
    const templateExpenses: ExpenseData = {
      officer_salary: Math.round(avgRevenue * defaults.officer_salary),
      rent: Math.round(avgRevenue * defaults.rent),
      utilities: Math.round(avgRevenue * defaults.utilities),
      insurance: Math.round(avgRevenue * defaults.insurance),
      other_fixed: Math.round(avgRevenue * defaults.other_fixed)
    };
    
    setExpenses(templateExpenses);
    setSources({
      officer_salary: 'template',
      rent: 'template',
      utilities: 'template',
      insurance: 'template',
      other_fixed: 'template'
    });
    setHasUnsavedChanges(true);
    
    if (onExpensesChange) {
      onExpensesChange(templateExpenses, {
        officer_salary: 'template',
        rent: 'template',
        utilities: 'template',
        insurance: 'template',
        other_fixed: 'template'
      });
    }
  };

  // Save expenses
  const handleSave = async () => {
    if (!onSave) return;
    
    setSaving(true);
    setSaveMessage(null);
    
    try {
      await onSave(expenses, sources);
      setSaveMessage('Expenses saved successfully!');
      setHasUnsavedChanges(false);
      setOriginalExpenses(expenses);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage('Failed to save expenses');
    } finally {
      setSaving(false);
    }
  };

  const renderSourceBadge = (source: 'verified' | 'estimate' | 'template', field: keyof ExpenseSource) => {
    const badge = SOURCE_BADGES[source];
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1" style={badge.color}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: badge.dotColor }} />
          {badge.label}
        </span>
        {source !== 'verified' && (
          <button
            onClick={() => handleUpgradeToVerified(field)}
            className="text-xs underline"
            style={{ color: 'var(--color-needs-human-text)' }}
            title="Mark as verified with documentation"
          >
            Verify
          </button>
        )}
      </div>
    );
  };

  const changeFromOriginal = calculateChangeFromOriginal();
  const totalClaimImpact = calculateTotalClaimImpact();

  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'transparent', border: '2px solid var(--color-verified)' }}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
            <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Maximize Your Claim - Enter Actual Expenses
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
              Continuing expenses like rent and officer salary increase recoverable loss
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {totalClaimImpact > 0 && (
            <div className="text-right">
              <p className="text-xs" style={{ color: 'var(--color-verified)' }}>Potential Claim Impact</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-verified)' }}><FinancialValue value={totalClaimImpact} /></p>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-4">
          {/* Info Banner */}
          <div className="rounded-lg p-3 flex items-start gap-2" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)' }}>
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
            <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
              <strong>Tip:</strong> Enter monthly expense amounts. These are fixed costs that continue during closure
              and are recoverable under business interruption policies. Higher documented expenses = higher claim value.
            </p>
          </div>

          {/* Quick Apply Template */}
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Business Type: <strong style={{ color: 'var(--color-text-primary)' }}>{reportData?.business_type || 'General'}</strong>
            </span>
            <button
              onClick={handleApplyTemplate}
              className="text-sm font-medium underline"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              Apply Industry Defaults (BLS Median)
            </button>
          </div>

          {/* Expense Entry Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Officer/Owner Salary */}
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Officer/Owner Salary</label>
                {renderSourceBadge(sources.officer_salary, 'officer_salary')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={expenses.officer_salary || ''}
                  onChange={(e) => handleExpenseChange('officer_salary', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 rounded-lg focus:ring-2"
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--color-text-dim)' }}>/mo</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Owner/officer compensation that continues during closure</p>
            </div>

            {/* Rent */}
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Monthly Rent</label>
                {renderSourceBadge(sources.rent, 'rent')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={expenses.rent || ''}
                  onChange={(e) => handleExpenseChange('rent', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 rounded-lg focus:ring-2"
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--color-text-dim)' }}>/mo</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Fixed lease payments required during closure</p>
            </div>

            {/* Utilities */}
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Utilities</label>
                {renderSourceBadge(sources.utilities, 'utilities')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={expenses.utilities || ''}
                  onChange={(e) => handleExpenseChange('utilities', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 rounded-lg focus:ring-2"
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--color-text-dim)' }}>/mo</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Minimum utility costs to maintain property</p>
            </div>

            {/* Insurance */}
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Insurance</label>
                {renderSourceBadge(sources.insurance, 'insurance')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={expenses.insurance || ''}
                  onChange={(e) => handleExpenseChange('insurance', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 rounded-lg focus:ring-2"
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--color-text-dim)' }}>/mo</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Property & liability insurance premiums</p>
            </div>

            {/* Other Fixed Expenses */}
            <div className="rounded-lg p-4 md:col-span-2" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Other Fixed Expenses</label>
                {renderSourceBadge(sources.other_fixed, 'other_fixed')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }}>$</span>
                <input
                  type="number"
                  value={expenses.other_fixed || ''}
                  onChange={(e) => handleExpenseChange('other_fixed', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 rounded-lg focus:ring-2"
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--color-text-dim)' }}>/mo</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Loan payments, equipment leases, software subscriptions, etc.</p>
            </div>
          </div>

          {/* Impact Preview */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'transparent', border: '1px solid var(--color-verified)' }}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Impact Preview ({closureDurationMonths} months)</h4>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-verified)' }}>Monthly Continuing Expenses</p>
                <p className="text-xl font-bold" style={{ color: 'var(--color-verified)' }}>
                  <FinancialValue value={calculateTotalMonthlyExpenses()} />
                </p>
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-verified)' }}>Total Claim Impact</p>
                <p className="text-xl font-bold" style={{ color: 'var(--color-verified)' }}>
                  <FinancialValue value={totalClaimImpact} />
                </p>
              </div>
              <div>
                <p className="text-xs mb-1" style={{ color: 'var(--color-verified)' }}>Change from Current</p>
                <p className="text-xl font-bold" style={{ color: changeFromOriginal >= 0 ? 'var(--color-verified)' : 'var(--color-text-muted)' }}>
                  {changeFromOriginal > 0 ? '+' : ''}<FinancialValue value={changeFromOriginal} />
                </p>
              </div>
            </div>

            {changeFromOriginal > 0 && (
              <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
                  <strong>Adding these expenses increases your demand by <FinancialValue value={changeFromOriginal} /></strong>
                </p>
              </div>
            )}
          </div>

          {/* Save Button */}
          {onSave && (
            <div className="flex items-center justify-between">
              {saveMessage && (
                <p className="text-sm" style={{ color: saveMessage.includes('Failed') ? 'var(--color-needs-human-text)' : 'var(--color-verified)' }}>
                  {saveMessage}
                </p>
              )}
              <div className="flex items-center gap-3 ml-auto">
                {hasUnsavedChanges && (
                  <span className="text-sm flex items-center gap-1" style={{ color: 'var(--color-brand)' }}>
                    <AlertCircle className="w-4 h-4" />
                    Unsaved changes
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all"
                  style={
                    saving || !hasUnsavedChanges
                      ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)', cursor: 'not-allowed' }
                      : { backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
                  }
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Apply & Recalculate'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
